<template>
  <div class="previewer">
    <div class="previewer__glow">
      <span class="previewer__glow-tl"></span>
      <span class="previewer__glow-tr"></span>
    </div>
    <div v-if="initLoaded === false" class="previewer__loading">
      <div class="loading-icon"></div>
      <div>Loading...</div>
    </div>
    <template v-else>
      <!-- Left sidebar: Back + Timeline (Desktop only) -->
      <div class="previewer__sidebar-left hidden-xs">
        <router-link :to="{name: 'shelf'}" class="previewer__back">
          <svg viewBox="0 0 48 48" width="18" height="18"><path fill="currentColor" d="M30.7 19.5c0-2.3-1.8-4.1-4.1-4.1H11.7C5.2 15.4 0 20.7 0 27.2v13.8C0 43.2 1.8 45 4.1 45s4.1-1.8 4.1-4.1V27.2c0-1.9 1.6-3.5 3.5-3.5h14.9c2.3 0 4.1-1.8 4.1-4.2z"/><path fill="currentColor" d="M28.8 0H4.2C1.8 0 0 1.8 0 4.1s1.8 4.1 4.1 4.1h24.5c6.1 0 11.1 5 11.1 11.1s-5 11.1-11.1 11.1h-10c-2.3 0-4.1 1.8-4.1 4.1s1.8 4.1 4.1 4.1h10C39.4 38.6 48 30 48 19.3S39.4 0 28.8 0z"/></svg>
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          {{$t('preview.buttonBack') || 'Back'}}
        </router-link>
        <div class="previewer__toc">
          <div class="previewer__toc-title">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="15" y2="18"/></svg>
            {{$t('preview.buttonTOC') || 'Contents'}}
          </div>
          <div class="previewer__toc-scroll">
            <div class="previewer__toc-cover" :class="{ active: isCover }" @click="goToCover">
              {{$t('preview.buttonCover') || 'Cover'}}
            </div>
            <div class="previewer__timeline timeline" v-if="isStart">
              <time-line :chapters="book.timeLine"
                         :activeYear="activeYear"
                         :activeMonth="activeMonth"
                         :clearSelection="isCover"
                         @changeMonth="movePage"
                         v-if="dataLoaded">
              </time-line>
            </div>
          </div>
        </div>
      </div>

      <!-- Book viewer -->
      <div v-if="navigating" class="previewer__nav-loading">
        <div class="loading-icon" style="transform: scale(1.2)"></div>
        <div class="text-muted" style="font-size: 14px">{{$t('preview.loading') || 'Loading...'}}</div>
      </div>
      <iframe id="turning-book" :src="previewLink" frameborder="0" @load="injectViewerStyles" :style="{ visibility: navigating ? 'hidden' : '' }"></iframe>

      <!-- Action buttons (Desktop) -->
      <div class="previewer__menu hidden-xs" v-show="initLoaded">
        <router-link v-if="canBuy" :to="{name: 'order-check', query: checkoutQuery}" class="previewer__menu-btn">
          <button class="btn primary">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/></svg>
            {{$t('preview.buttonPurchase')}}
          </button>
        </router-link>
        <div class="previewer__menu-divider"></div>
        <template v-if="!$route.query.locked">
          <div class="previewer__menu-btn" @click="openEditor('content')">
            <button class="btn default-ghost">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              {{$t('preview.buttonEdit')}}
            </button>
          </div>
          <div class="previewer__menu-btn" @click="openEditor('cover')">
            <button class="btn default-ghost">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
              {{$t('preview.buttonDesign')}}
            </button>
          </div>
        </template>
      </div>

      <!-- Mobile: Back (top-left) -->
      <router-link :to="{name: 'shelf'}" class="previewer__m-back" v-show="initLoaded">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>
        {{$t('preview.buttonBack') || 'Back'}}
      </router-link>

      <!-- Mobile: Buy (top-right) -->
      <router-link v-if="canBuy" :to="{name: 'order-check', query: checkoutQuery}" class="previewer__m-buy" v-show="initLoaded">
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/></svg>
        {{$t('preview.buttonPurchase')}}
      </router-link>

      <!-- Mobile: TOC (bottom-left) -->
      <div class="previewer__m-toc" v-show="initLoaded && !showEditor">
        <div class="previewer__m-toc-panel" v-if="showMobileTOC">
          <div class="previewer__m-toc-title">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="15" y2="18"/></svg>
            {{$t('preview.buttonTOC') || 'Contents'}}
          </div>
          <div class="previewer__toc-cover" :class="{ active: isCover }" @click="goToCover(); showMobileTOC = false">{{$t('preview.buttonCover') || 'Cover'}}</div>
          <time-line v-if="isStart && dataLoaded"
                     :chapters="book.timeLine"
                     :activeYear="activeYear"
                     :activeMonth="activeMonth"
                     :clearSelection="isCover"
                     @changeMonth="movePage($event); showMobileTOC = false"/>
        </div>
        <button class="previewer__m-fab" @click="showMobileTOC = !showMobileTOC; showMobileActions = false">
          <svg v-if="!showMobileTOC" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="15" y2="18"/></svg>
          <svg v-else viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      <!-- Mobile: Edit (bottom-right) -->
      <div class="previewer__m-edit" v-show="initLoaded && !showEditor && !$route.query.locked">
        <div class="previewer__m-edit-panel" v-if="showMobileActions">
          <button @click="openEditor('content'); showMobileActions = false">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            {{$t('preview.buttonEdit')}}
          </button>
          <button @click="openEditor('cover'); showMobileActions = false">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
            {{$t('preview.buttonDesign')}}
          </button>
        </div>
        <button class="previewer__m-fab" @click="showMobileActions = !showMobileActions; showMobileTOC = false">
          <svg v-if="!showMobileActions" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
          <svg v-else viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      <!-- Content Editor Overlay -->
      <transition name="ce-slide">
        <content-editor v-if="showEditor"
                        :book="book"
                        :design="book.design || {}"
                        :chapters="book.chapters || []"
                        :initialPanel="editorPanel"
                        :initialYear="activeYear"
                        :initialMonth="activeMonth"
                        @close="closeEditor"
                        @save-preview="saveAndReload"/>
      </transition>
    </template>
  </div>
</template>

<script>
import api from 'src/api'
import TimeLine from '../Content/Timeline.vue'
import ContentEditor from './ContentEditor.vue'
import FBShare from 'src/mixins/FBShare'
import resolveBookData from '../resolveBookData'
import { mapGetters } from 'vuex'

export default {
  name: 'preview',
  components: { TimeLine, ContentEditor },
  mixins: [FBShare],
  data () {
    return {
      page: 1,
      currentPage: 1,
      channel: null,
      otherWindow: null,
      dataLoaded: false,
      bookLoaded: false,
      book: {},
      activeYear: '',
      activeMonth: '',
      isStart: false,
      isCover: true,
      navigating: false,
      showEditor: false,
      editorPanel: 'content',
      showMobileTOC: false,
      showMobileActions: false,
      initLoaded: false,
      interval: null,
      bid: ''
    }
  },
  computed: {
    ...mapGetters([
      'user'
    ]),
    canBuy () {
      return ['a5-1', 'a5-2', 'a4-1', 'a4-2'].includes(this.book.typeset)
    },
    checkoutQuery () {
      return {
        ...this.$route.query,
        bookType: this.$route.query.bookType || this.book.type || 'diarybook'
      }
    },
    previewLink () {
      const {
        query,
        params
      } = this.$route
      const bid = query.bid || params.bid
      return `/viewer/?bid=${bid}`
    }
  },
  watch: {
    currentPage (page) {
      if (!this.book.timeLine || !page) return
      // Cover check
      if (page <= 2) {
        this.isCover = true
        this.activeYear = ''
        this.activeMonth = ''
        return
      }
      // Find which month this page belongs to
      this.isCover = false
      const timeLine = this.book.timeLine
      let matchedYear = ''
      let matchedMonth = ''
      const allMonths = []
      Object.keys(timeLine).forEach(year => {
        timeLine[year].forEach(m => {
          allMonths.push({ year, month: m.month, time: m.time, pages: m.pages })
        })
      })
      allMonths.sort((a, b) => a.pages - b.pages)
      for (let i = 0; i < allMonths.length; i++) {
        const curr = allMonths[i]
        const next = allMonths[i + 1]
        if (page >= curr.pages && (!next || page < next.pages)) {
          matchedYear = curr.year
          matchedMonth = curr.month
          break
        }
      }
      if (matchedYear) {
        this.activeYear = matchedYear
        this.activeMonth = matchedMonth
      }
    }
  },
  beforeDestroy () {
    clearInterval(this.interval)
  },
  created () {
    const bid = this.$route.params.bid || this.$route.query.bid
    this.bid = bid
    this.triggerTypeset(bid)
  },
  mounted () {
    this.init()
  },
  methods: {
    async init () {
      try {
        let result
        let i = 0
        while (!result) {
          result = await this.loadBook()
          await new Promise(resolve => setTimeout(resolve, 1000))
          i++
          if (i > 50) {
            return
          }
        }
        this.book = result.book
        this.years = result.years
        this.initLoaded = true
        if (this.book.timeLine) {
          const years = Object.keys(this.book.timeLine)
          // Default to the oldest year
          this.activeYear = years[years.length - 1] || years[0]
          const months = this.book.timeLine[this.activeYear]
          this.activeMonth = months ? months[months.length - 1].month : ''
          // Auto open timeline
          this.isStart = true
        }
        // Auto open editor if edit=1 from shelf
        if (this.$route.query.edit === '1') {
          this.showEditor = true
        }
      } finally {
        this.$nextTick(() => {
          const iframe = this.$el.querySelector('#turning-book')
          if (!iframe) return
          const onReady = () => {
            this.injectViewerStyles()
            // Wait for viewer's Vue app to initialize before setting up channel
            const waitForViewer = () => {
              const bc = this.getBookComp(iframe.contentWindow)
              if (!bc) { setTimeout(waitForViewer, 500); return }
              this.setupChannel()
            }
            setTimeout(waitForViewer, 1000)
          }
          // If iframe already loaded, setup now; otherwise wait for load
          if (iframe.contentDocument && iframe.contentDocument.readyState === 'complete') {
            onReady()
          } else {
            iframe.addEventListener('load', onReady, { once: true })
          }
        })
      }
    },
    async loadBook () {
      if (this.fetching) {
        return
      }
      try {
        this.fetching = true
        const data = await api.fetchBook(this.bid)
        const retypesetMonth = data.retypesetMonths[data.typeset]
        if (!retypesetMonth || !retypesetMonth.length) {
          this.dataLoaded = true
          return resolveBookData(data)
        }
      } finally {
        this.fetching = false
      }
    },
    triggerTypeset (bid) {
      return api.triggerTypeset(bid)
    },
    // Poll book status until retypesetMonths is empty (all months typeset)
    // Returns as soon as typesetting is done, or after maxWait ms
    async waitForTypeset (maxWait = 60000) {
      const start = Date.now()
      const poll = async () => {
        if (Date.now() - start > maxWait) return // timeout safety
        try {
          const data = await api.fetchBook(this.bid)
          const pending = data.retypesetMonths && data.retypesetMonths[data.typeset]
          if (!pending || !pending.length) return // done
        } catch (e) {
          // ignore fetch errors, keep polling
        }
        await new Promise(resolve => setTimeout(resolve, 2000))
        return poll()
      }
      return poll()
    },
    movePage (month) {
      if (month.deleted) {
        this.$confirm({
          message: '該月份被隱藏，是否恢復'
        })
        return
      }
      this.isCover = false
      this.navigateViewer(month.pages)
    },
    navigateViewer (page) {
      const iframe = this.$el.querySelector('#turning-book')
      if (!iframe) return
      const win = iframe.contentWindow
      const bookComp = this.getBookComp(win)
      const current = bookComp ? (bookComp.currentPage || 1) : -1
      const distance = Math.abs(current - page)
      if (bookComp && distance <= 5) {
        // Near: step-navigate directly
        this.stepNavigate(bookComp, current, page)
      } else {
        // Far: reload then step from page 1
        this.reloadAndNavigate(iframe, page)
      }
    },
    stepNavigate (bookComp, from, to) {
      if (from === to) return
      const step = from < to ? 2 : -2
      let pos = from
      const interval = setInterval(() => {
        if ((step > 0 && pos >= to) || (step < 0 && pos <= to)) {
          clearInterval(interval)
          bookComp.movePage({ target: { value: to } })
          return
        }
        pos = step > 0 ? Math.min(pos + step, to) : Math.max(pos + step, to)
        bookComp.movePage({ target: { value: pos } })
      }, 100)
    },
    getBookComp (win) {
      try {
        if (!win || !win.$nuxt) return null
        const find = (comp, depth) => {
          if (depth > 8) return null
          if ((comp.$options.methods || {}).movePage) return comp
          for (const child of (comp.$children || [])) {
            const found = find(child, depth + 1)
            if (found) return found
          }
          return null
        }
        return find(win.$nuxt, 0)
      } catch (e) { return null }
    },
    reloadAndNavigate (iframe, page) {
      const src = iframe.src.replace(/[#].*$/, '')
      this.navigating = true
      iframe.addEventListener('load', () => {
        this.otherWindow = iframe.contentWindow
        this.injectViewerStyles()
        const waitAndNav = () => {
          const bc = this.getBookComp(iframe.contentWindow)
          if (!bc) { setTimeout(waitAndNav, 500); return }
          this.setupChannel()
          let pos = 1
          const interval = setInterval(() => {
            if (pos >= page) {
              clearInterval(interval)
              bc.movePage({ target: { value: page } })
              setTimeout(() => { this.navigating = false }, 300)
              return
            }
            pos = Math.min(pos + 2, page)
            bc.movePage({ target: { value: pos } })
          }, 50)
        }
        setTimeout(waitAndNav, 1500)
      }, { once: true })
      iframe.contentWindow.location.replace(src)
    },
    injectViewerStyles () {
      try {
        const iframe = this.$el.querySelector('#turning-book')
        const doc = iframe.contentDocument || iframe.contentWindow.document
        const style = doc.createElement('style')
        style.textContent = `
          .indicator {
            height: 3px !important;
            box-shadow: none !important;
            border-radius: 3px !important;
          }
          input[type=range]::-webkit-slider-thumb {
            height: 14px !important;
            width: 14px !important;
            background: linear-gradient(135deg, #f78287, #F56368) !important;
            box-shadow: 0 1px 4px rgba(81,45,202,.4) !important;
          }
          input[type=range]::-moz-range-thumb {
            height: 14px !important;
            width: 14px !important;
            background: linear-gradient(135deg, #f78287, #F56368) !important;
            box-shadow: 0 1px 4px rgba(81,45,202,.4) !important;
          }
          .book-loader svg, .book-loader img {
            display: none !important;
          }
          .book-loader::before, .book-loader::after {
            content: '';
            position: absolute;
            width: 20px;
            height: 32px;
            top: 50%;
            left: 50%;
            margin-top: -16px;
            margin-left: -10px;
            background: #F56368;
            border-radius: 0 4px 4px 0;
            transform-origin: left center;
            animation: book-flip 1.2s ease-in-out infinite;
          }
          .book-loader::before { opacity: 0.5; animation-delay: 0s; }
          .book-loader::after { opacity: 0.8; animation-delay: 0.2s; }
          .book-loader { position: relative; min-height: 40px; }
          .book-loader span { color: #F56368 !important; }
          @keyframes book-flip {
            0%, 100% { transform: rotateY(0deg); }
            50% { transform: rotateY(-180deg); }
          }
        `
        doc.head.appendChild(style)
      } catch (e) {
        // iframe may not be ready or cross-origin
      }
    },
    goToCover () {
      if (this.isCover) return
      const iframe = this.$el.querySelector('#turning-book')
      if (!iframe) return
      const win = iframe.contentWindow
      const bookComp = this.getBookComp(win)
      const $ = win.$ || win.jQuery
      const current = bookComp ? (bookComp.currentPage || 1) : 999
      if (current <= 5 && bookComp && $) {
        // Near: step back then close
        const turnEl = $(iframe.contentDocument).find('#turn')
        let pos = current
        const interval = setInterval(() => {
          if (pos <= 2) {
            clearInterval(interval)
            setTimeout(() => { turnEl.turn('previous') }, 200)
            return
          }
          pos = Math.max(pos - 2, 2)
          bookComp.movePage({ target: { value: pos } })
        }, 100)
      } else {
        // Far: reload iframe (naturally shows closed cover)
        const src = iframe.src.replace(/[#].*$/, '')
        iframe.addEventListener('load', () => {
          this.otherWindow = iframe.contentWindow
          this.injectViewerStyles()
          this.setupChannel()
        }, { once: true })
        win.location.replace(src)
      }
      this.isCover = true
    },
    startRead () {
      this.navigateViewer(3)
      this.isStart = true
    },
    openEditor (panel) {
      this.editorPanel = panel || 'content'
      this.showEditor = true
    },
    closeEditor () {
      this.showEditor = false
    },
    async saveAndReload (changed) {
      this.showEditor = false
      if (!changed) return
      // Content was modified — re-typeset and reload the book
      this.navigating = true
      await this.triggerTypeset(this.bid)
      // Wait for typesetting to complete before loading preview
      await this.waitForTypeset()
      // Reload book data to get updated timeLine (chapters may have changed)
      const freshData = await this.loadBook()
      if (freshData) {
        this.book = freshData.book
        this.years = freshData.years
      }
      // Reset timeline to cover
      this.isCover = true
      this.currentPage = 1
      if (this.book.timeLine) {
        const years = Object.keys(this.book.timeLine)
        this.activeYear = years[years.length - 1] || years[0]
        const months = this.book.timeLine[this.activeYear]
        this.activeMonth = months ? months[months.length - 1].month : ''
      }
      // Reload iframe from scratch (shows closed cover)
      const iframe = this.$el.querySelector('#turning-book')
      if (!iframe) return
      iframe.addEventListener('load', () => {
        this.injectViewerStyles()
        const waitForBook = () => {
          const bc = this.getBookComp(iframe.contentWindow)
          if (!bc) { setTimeout(waitForBook, 500); return }
          this.setupChannel()
          this.navigating = false
        }
        setTimeout(waitForBook, 1500)
      }, { once: true })
      // Add cache-busting timestamp to force fresh data
      const base = iframe.src.replace(/[#].*$/, '').replace(/[&?]_t=\d+/, '')
      const sep = base.includes('?') ? '&' : '?'
      iframe.contentWindow.location.replace(base + sep + '_t=' + Date.now())
    },
    setupChannel () {
      const iframe = this.$el.querySelector('#turning-book')
      if (!iframe || !iframe.contentWindow) return
      this.otherWindow = iframe.contentWindow
      this.channel = new MessageChannel()
      this.channel.port1.onmessage = (msg) => {
        const { data } = msg
        if (data.bid) {
          const { totalPage } = data
          this.page = totalPage % 2 === 0 ? totalPage + 4 : data + 5
          const {
            book,
            bookLoaded,
            years
          } = resolveBookData(data)
          this.bookLoaded = bookLoaded
          this.years = years
          this.book = book
        } else {
          this.currentPage = data.currentPage
        }
      }
      this.otherWindow.postMessage('initialize', '*', [this.channel.port2])
    }
  }
}
</script>

<style lang="scss">
@import '../../../styles/variables';

.container.preview-bg {
  width: 1170px;
  @media (max-width: 1279px) {
    width: 100%;
    .previewer__timeline, .previewer__menu {
      transform: translateX(0);
    }
  }
}

.slide-up-enter-active, .slide-up-leave-active {
  transition: transform .5s;
  will-change: transform;
}

.slide-up-leave-to {
  transform: translateY(-100%);
}

.previewer {
  position: relative;
  flex: 1;
  flex-grow: 1;
  width: 100%;
  display: flex;
  margin-top: -0.5rem;

  @media (max-width: 767px) {
    margin-top: 0;
    align-items: center;
    justify-content: center;
  }

  &__loading {
    margin: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }

  &__sidebar-left {
    position: absolute;
    left: 0;
    top: 5%;
    bottom: 5%;
    transform: translateX(-100%);
    z-index: 5;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    text-align: right;
    max-height: 90%;

    @media (max-width: 1279px) {
      transform: translateX(0);
    }
  }

  &__back {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    padding: 8px 16px;
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    text-decoration: none;
    border-radius: 8px;
    transition: all 0.2s;
    white-space: nowrap;
    margin-bottom: 0.5rem;

    &:hover {
      color: $el-color-primary;
      background: rgba(81, 45, 202, 0.05);
    }
  }

  &__toc {
    width: 100%;
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;

    &-scroll {
      flex: 1;
      overflow-y: auto;
      overflow-x: hidden;
      -webkit-overflow-scrolling: touch;

      &::-webkit-scrollbar {
        width: 3px;
      }
      &::-webkit-scrollbar-thumb {
        background: rgba(0, 0, 0, 0.15);
        border-radius: 3px;
      }
    }

    &-title {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 6px;
      padding: 8px 16px;
      font-size: 14px;
      font-weight: 600;
      color: #1a1a2e;
      cursor: pointer;
      border-bottom: 2px solid $el-color-primary;
      margin-bottom: 0.5rem;
    }

    &-cover {
      padding: 6px 16px;
      font-size: 14px;
      cursor: pointer;
      text-align: right;
      color: #6b7280;
      border-right: 2px solid transparent;
      transition: all 0.2s;

      &:hover {
        color: $el-color-primary;
      }

      &.active {
        color: $el-color-primary;
        font-weight: 600;
        border-right-color: $el-color-primary;
      }
    }
  }

  .previewer__timeline {
    width: 100%;
  }

  .previewer__menu {
    position: absolute;
    right: 0;
    top: 5%;
    transform: translateX(100%);
    z-index: 5;
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 130px;

    .previewer__menu-btn {
      display: block;
      text-decoration: none;

      .btn {
        width: 100%;
        justify-content: center;
        padding: 10px 16px;
        font-size: 13px;
        font-weight: 600;

        svg {
          flex-shrink: 0;
        }
      }

      .btn.default-ghost {
        background: #fff;
        color: $el-color-primary;
        border: 1.5px solid $el-color-primary;

        &:hover {
          background: $el-color-primary;
          color: #fff;
        }
      }
    }

    .previewer__menu-divider {
      height: 1px;
      background: #e0e0e0;
      margin: 4px 0;
    }
  }

  #turning-book {
    height: 100%;
    flex: 1;
    position: relative;
    z-index: 1;
  }

  &__glow {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    z-index: 0;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      bottom: -10%;
      left: -10%;
      width: 45%;
      height: 50%;
      background: radial-gradient(ellipse at center, rgba(200, 180, 240, 0.5) 0%, rgba(180, 210, 255, 0.3) 40%, transparent 70%);
      border-radius: 50%;
      filter: blur(60px);
    }

    &::after {
      content: '';
      position: absolute;
      bottom: -10%;
      right: -10%;
      width: 45%;
      height: 50%;
      background: radial-gradient(ellipse at center, rgba(170, 230, 220, 0.42) 0%, rgba(180, 240, 210, 0.22) 40%, transparent 70%);
      border-radius: 50%;
      filter: blur(60px);
    }
  }

  &__glow-tl {
    position: absolute;
    top: -10%;
    left: -10%;
    width: 40%;
    height: 45%;
    background: radial-gradient(ellipse at center, rgba(180, 210, 255, 0.4) 0%, rgba(200, 180, 240, 0.2) 40%, transparent 70%);
    border-radius: 50%;
    filter: blur(60px);
  }

  &__glow-tr {
    position: absolute;
    top: -10%;
    right: -10%;
    width: 40%;
    height: 45%;
    background: radial-gradient(ellipse at center, rgba(180, 240, 220, 0.35) 0%, rgba(170, 230, 210, 0.18) 40%, transparent 70%);
    border-radius: 50%;
    filter: blur(60px);
  }

  &__nav-loading {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 1rem;
    z-index: 10;
  }

  // Mobile-only elements: hidden on desktop
  &__m-back, &__m-buy, &__m-toc, &__m-edit {
    display: none !important;
  }

  @media (max-width: 767px) {
    // iframe 不再撑满，自适应

    &__m-back {
      display: flex !important;
      position: fixed;
      top: 10px;
      left: 10px;
      z-index: 20;
      align-items: center;
      gap: 2px;
      padding: 6px 14px 6px 8px;
      color: #374151;
      border-radius: 100px;
      background: rgba(255, 255, 255, 0.85);
      backdrop-filter: blur(6px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      text-decoration: none;
      font-size: 13px;
      font-weight: 500;
    }

    &__m-buy {
      display: flex !important;
      position: fixed;
      top: 10px;
      right: 10px;
      z-index: 20;
      align-items: center;
      gap: 4px;
      padding: 6px 14px;
      background: $el-color-primary;
      color: #fff;
      border-radius: 100px;
      text-decoration: none;
      box-shadow: 0 2px 8px rgba(81, 45, 202, 0.3);
      font-size: 13px;
      font-weight: 600;

      &:hover { color: #fff; }
    }

    &__m-toc {
      display: flex !important;
      position: fixed;
      left: 20px;
      bottom: 50px;
      z-index: 20;
      flex-direction: column;
      align-items: flex-start;
    }

    &__m-edit {
      display: flex !important;
      position: fixed;
      right: 20px;
      bottom: 50px;
      z-index: 20;
      flex-direction: column;
      align-items: flex-end;
    }
  }

  // Shared FAB style
  &__m-fab {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    border: none;
    background: $el-color-primary;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 16px rgba(81, 45, 202, 0.35);
    cursor: pointer;
    transition: transform 0.15s;
    flex-shrink: 0;

    &:active { transform: scale(0.92); }
  }

  &__m-toc-title {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
    padding: 10px 16px 8px;
    font-size: 13px;
    font-weight: 600;
    color: #1a1a2e;
    border-bottom: 2px solid $el-color-primary;
    margin: 0 8px 6px;
  }

  &__m-toc-panel {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
    margin-bottom: 16px;
    max-height: 50vh;
    min-width: 140px;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    padding: 6px 0;

    .previewer__toc-cover {
      text-align: right;
      padding: 8px 16px;
      font-size: 14px;
    }

    .timeline {
      display: block;
      text-align: right;

      .tl-action-btn { display: none !important; }
      .year-row { justify-content: flex-end; }
      .year-label { flex: none !important; width: 80px !important; text-align: center !important; box-sizing: border-box !important; }
      .months li { justify-content: flex-end; }
      .months li a { flex: none !important; }
      .hidden-section { display: none; }
    }
  }

  &__m-edit-panel {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
    margin-bottom: 16px;
    min-width: 140px;
    overflow: hidden;

    button {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      padding: 14px 20px;
      border: none;
      background: none;
      font-size: 15px;
      font-weight: 500;
      color: #374151;
      cursor: pointer;
      white-space: nowrap;

      &:active { background: #f3f4f6; }
      & + button { border-top: 1px solid #f3f4f6; }
    }
  }
}

// Panel expand animation
.m-pop-enter-active, .m-pop-leave-active {
  transition: opacity 0.2s, transform 0.2s;
  transform-origin: center bottom;
}
.m-pop-enter, .m-pop-leave-to {
  opacity: 0;
  transform: scale(0.9) translateY(8px);
}
</style>
